export const OUTBOUND_PREPARATION_TIMEOUT_MS = 120_000;

export function getSdkOutboundAttemptId(callInfo: { extraHeaders?: Record<string, unknown> } | null | undefined): string | undefined {
  const header = Object.entries(callInfo?.extraHeaders || {}).find(([name]) => name.toLowerCase() === "x-ph-outboundattemptid")?.[1];
  return typeof header === "string" ? header : undefined;
}

export function acceptSdkCallEvent(
  guard: OutboundAttemptGuard,
  callInfo: { callUUID?: string | null; extraHeaders?: Record<string, unknown> } | null | undefined,
  incomingUuid: string | null,
  terminal = false,
): boolean {
  const uuid = callInfo?.callUUID || null;
  const attemptId = getSdkOutboundAttemptId(callInfo);
  if (guard.id && !guard.started) return false;
  if (guard.id || guard.isRetired(uuid)) return guard.accept(uuid, terminal, attemptId);
  // SIP headers may be forwarded from the originating browser through an IVR,
  // queue or extension bridge. That browser's outbound token does not make the
  // receiving browser an outbound owner. Match its registered incoming leg first.
  if (incomingUuid) return incomingUuid === uuid;
  // Without an incoming leg or an owned outbound attempt, a token-bearing event
  // is stale/foreign and must not attach itself to the next call.
  return !attemptId;
}

/** One owner from the first synchronous dial instruction through termination. */
export class OutboundAttemptGuard {
  private active: { id: string; uuid: string | null; started: boolean } | null = null;
  private retiredUuids = new Set<string>();

  acquire(id: string, idle: boolean): boolean {
    if (this.active || !idle) return false;
    this.active = { id, uuid: null, started: false };
    return true;
  }

  owns(id: string): boolean { return this.active?.id === id; }
  get id(): string | null { return this.active?.id || null; }
  get started(): boolean { return this.active?.started === true; }
  get bound(): boolean { return !!this.active?.uuid; }
  markStarted(id: string): void { if (this.owns(id) && this.active) this.active.started = true; }
  isRetired(uuid: string | null): boolean { return !!uuid && this.retiredUuids.has(uuid); }

  accept(uuid: string | null, terminal = false, attemptId?: string): boolean {
    if (!this.active || (attemptId && !this.owns(attemptId)) || (uuid && this.retiredUuids.has(uuid))) return false;
    if (!uuid) return terminal && this.started && !!attemptId && this.owns(attemptId);
    if (this.active.uuid && this.active.uuid !== uuid) return false;
    // An unbound terminal event needs the exact started attempt's SIP token.
    if (terminal && !this.active.uuid && (!this.started || !attemptId)) return false;
    this.active.uuid = uuid;
    return true;
  }

  release(id: string): void {
    if (!this.owns(id)) return;
    if (this.active?.uuid) this.retiredUuids.add(this.active.uuid);
    this.active = null;
  }
}
